import React, { useState, useEffect } from 'react';
import {
  getContractValueLog,
  getContractValueSummary,
  createManualAdjustment,
  getChangeOrders,
  createChangeOrder,
  updateChangeOrder,
  deleteChangeOrder,
  getSOVProjectChangeLog,
} from '../api';
import ConfirmDialog from './ConfirmDialog';
import useConfirm from '../hooks/useConfirm';
import { useTheme } from '../hooks/useTheme';

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '$0.00';
  const num = parseFloat(value);
  const sign = num < 0 ? '-' : '';
  return sign + '$' + Math.abs(num).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDate = (dateString) => {
  if (!dateString) return '–';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
};

const formatDateTime = (dateString) => {
  if (!dateString) return '–';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
};

const getStatusBadge = (status) => {
  switch (status) {
    case 'Draft':
      return { backgroundColor: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0' };
    case 'Submitted':
      return { backgroundColor: '#fef3c7', color: '#d97706', border: '1px solid #fcd34d' };
    case 'Approved':
      return { backgroundColor: '#dcfce7', color: '#16a34a', border: '1px solid #86efac' };
    case 'Rejected':
      return { backgroundColor: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5' };
    default:
      return { backgroundColor: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0' };
  }
};

const getChangeTypeBadge = (type) => {
  switch (type) {
    case 'Change Order':
      return { backgroundColor: '#dbeafe', color: '#1d4ed8', border: '1px solid #93c5fd' };
    case 'Manual Adjustment':
      return { backgroundColor: '#fef3c7', color: '#d97706', border: '1px solid #fcd34d' };
    case 'Original Contract':
      return { backgroundColor: '#dcfce7', color: '#16a34a', border: '1px solid #86efac' };
    default:
      return { backgroundColor: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0' };
  }
};

const ContractValueTab = ({ projectId, refreshSummary }) => {
  const t = useTheme();
  const { confirm, confirmDialogProps } = useConfirm();
  const [summary, setSummary] = useState(null);
  const [log, setLog] = useState([]);
  const [changeOrders, setChangeOrders] = useState([]);
  const [sovChangeLog, setSovChangeLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');

  // Change Order form
  const [showCOModal, setShowCOModal] = useState(false);
  const [coForm, setCOForm] = useState({
    change_order_number: '', title: '', description: '', requested_amount: '', notes: '',
  });

  // Approve modal
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [selectedCO, setSelectedCO] = useState(null);
  const [approvedAmount, setApprovedAmount] = useState('');

  // Manual adjustment form
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [adjustForm, setAdjustForm] = useState({
    description: '', amount_change: '', changed_by: '',
  });

  useEffect(() => {
    fetchAll();
  }, [projectId]);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [summaryData, logData, coData, sovLogData] = await Promise.all([
        getContractValueSummary(projectId),
        getContractValueLog(projectId),
        getChangeOrders(projectId),
        getSOVProjectChangeLog(projectId),
      ]);
      setSummary(summaryData);
      setLog(logData);
      setChangeOrders(coData);
      setSovChangeLog(sovLogData);
    } catch (error) {
      console.error('Error fetching contract value data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCO = async (e) => {
    e.preventDefault();
    try {
      await createChangeOrder({
        ...coForm,
        project_id: projectId,
        status: 'Draft',
      });
      setCOForm({ change_order_number: '', title: '', description: '', requested_amount: '', notes: '' });
      setShowCOModal(false);
      await fetchAll();
      refreshSummary();
    } catch (error) {
      console.error('Error creating change order:', error);
    }
  };

  const handleStatusChange = async (co, newStatus) => {
    if (newStatus === 'Approved') {
      setSelectedCO(co);
      setApprovedAmount(co.requested_amount || '');
      setShowApproveModal(true);
    } else {
      try {
        await updateChangeOrder(co.id, { status: newStatus });
        await fetchAll();
        refreshSummary();
      } catch (error) {
        console.error('Error updating change order:', error);
      }
    }
  };

  const handleApprove = async () => {
    try {
      await updateChangeOrder(selectedCO.id, {
        status: 'Approved',
        approved_amount: parseFloat(approvedAmount),
      });
      setShowApproveModal(false);
      setSelectedCO(null);
      setApprovedAmount('');
      await fetchAll();
      refreshSummary();
    } catch (error) {
      console.error('Error approving change order:', error);
    }
  };

  const handleDeleteCO = async (coId) => {
    const ok = await confirm({
      title: 'Delete Change Order',
      message: 'Are you sure you want to delete this change order? This action cannot be undone.',
      confirmLabel: 'Delete', danger: true,
    });
    if (ok) {
      try {
        await deleteChangeOrder(coId);
        await fetchAll();
        refreshSummary();
      } catch (error) {
        console.error('Error deleting change order:', error);
      }
    }
  };

  const handleManualAdjustment = async (e) => {
    e.preventDefault();
    try {
      await createManualAdjustment({
        project_id: projectId,
        description: adjustForm.description,
        amount_change: parseFloat(adjustForm.amount_change),
        changed_by: adjustForm.changed_by,
      });
      setAdjustForm({ description: '', amount_change: '', changed_by: '' });
      setShowAdjustModal(false);
      await fetchAll();
      refreshSummary();
    } catch (error) {
      console.error('Error creating manual adjustment:', error);
    }
  };

  // Styles
  const mInput = {
    width: '100%', padding: '10px 14px', fontSize: '14px',
    border: `1px solid ${t.cardBorder}`, borderRadius: '10px', outline: 'none',
    transition: 'border-color 0.2s', backgroundColor: t.bg, boxSizing: 'border-box',
  };
  const mLabel = { display: 'block', fontSize: '14px', fontWeight: 600, color: t.heading, marginBottom: '6px' };
  const mGroup = { marginBottom: '14px' };
  const cardStyle = {
    flex: 1, minWidth: '160px', padding: '16px 20px',
    borderRadius: '12px', border: `1px solid ${t.cardBorder}`, backgroundColor: t.bg,
  };
  const cardLabelStyle = {
    fontSize: '11px', fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: '0.5px', color: t.textSecondary, marginBottom: '6px',
  };
  const cardValueStyle = { fontSize: '22px', fontWeight: 700, color: t.heading };
  const cardSubStyle = { fontSize: '12px', color: t.textSecondary, marginTop: '2px' };

  const sectionTabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'changeOrders', label: 'Change Orders' },
    { key: 'log', label: 'Audit Log' },
    { key: 'sovChanges', label: 'SOV Changes' },
  ];

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center', color: t.textSecondary }}>Loading...</div>;
  }

  const approvedCOs = changeOrders.filter(co => co.status === 'Approved');
  const pendingCOs = changeOrders.filter(co => co.status === 'Draft' || co.status === 'Submitted');
  const pendingAmount = pendingCOs.reduce((s, co) => s + (parseFloat(co.requested_amount) || 0), 0);

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: t.heading }}>
            Contract Value
          </h3>
          <p style={{ margin: '4px 0 0', fontSize: '13px', color: t.textSecondary }}>
            Track contract value changes, change orders, and manual adjustments.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={() => setShowAdjustModal(true)} style={{
            padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: 600,
            border: `1px solid ${t.cardBorder}`, backgroundColor: t.bg, color: t.heading, cursor: 'pointer',
          }}>
            + Manual Adjustment
          </button>
          <button onClick={() => setShowCOModal(true)} style={{
            padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: 600,
            border: 'none', backgroundColor: '#3b82f6', color: '#fff', cursor: 'pointer',
          }}>
            + Change Order
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <div style={cardStyle}>
            <div style={cardLabelStyle}>Original Contract</div>
            <div style={cardValueStyle}>{formatCurrency(summary.original_contract_value)}</div>
            <div style={cardSubStyle}>base value</div>
          </div>
          <div style={{ ...cardStyle, borderColor: '#93c5fd', backgroundColor: '#f0f7ff' }}>
            <div style={cardLabelStyle}>Approved COs</div>
            <div style={{ ...cardValueStyle, color: '#1d4ed8' }}>{formatCurrency(summary.approved_change_orders_total)}</div>
            <div style={cardSubStyle}>{approvedCOs.length} approved</div>
          </div>
          <div style={{ ...cardStyle, borderColor: pendingCOs.length > 0 ? '#fcd34d' : '#e2e8f0', backgroundColor: pendingCOs.length > 0 ? '#fffbeb' : '#fff' }}>
            <div style={cardLabelStyle}>Pending COs</div>
            <div style={{ ...cardValueStyle, color: pendingCOs.length > 0 ? '#d97706' : '#94a3b8' }}>{formatCurrency(pendingAmount)}</div>
            <div style={cardSubStyle}>{pendingCOs.length} pending</div>
          </div>
          <div style={{ ...cardStyle, borderColor: '#86efac', backgroundColor: '#f0fdf4' }}>
            <div style={cardLabelStyle}>Current Contract Value</div>
            <div style={{ ...cardValueStyle, color: '#16a34a' }}>{formatCurrency(summary.revised_contract_value)}</div>
            <div style={cardSubStyle}>revised total</div>
          </div>
        </div>
      )}

      {/* Section Tabs */}
      <div style={{ display: 'flex', gap: '0', marginBottom: '16px', borderBottom: `2px solid ${t.tableBorder}` }}>
        {sectionTabs.map(tab => (
          <button key={tab.key} onClick={() => setActiveSection(tab.key)} style={{
            padding: '10px 20px', fontSize: '13px', fontWeight: 600,
            border: 'none', backgroundColor: 'transparent', cursor: 'pointer',
            color: activeSection === tab.key ? '#3b82f6' : t.textSecondary,
            borderBottom: activeSection === tab.key ? '2px solid #3b82f6' : '2px solid transparent',
            marginBottom: '-2px', transition: 'all 0.15s',
          }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Section */}
      {activeSection === 'overview' && summary && (
        <div style={{ border: `1px solid ${t.cardBorder}`, borderRadius: '12px', overflow: 'hidden', backgroundColor: t.bg }}>
          <div style={{
            padding: '14px 20px', backgroundColor: t.bgAlt, borderBottom: `2px solid ${t.tableBorder}`,
            fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: t.textSecondary,
          }}>
            Contract Value Breakdown
          </div>
          <div style={{ padding: '20px' }}>
            {/* Breakdown rows */}
            {[
              { label: 'Original Contract Value', value: summary.original_contract_value, color: t.heading, weight: 600 },
              ...approvedCOs.map(co => ({
                label: `CO ${co.change_order_number}: ${co.title}`,
                value: co.approved_amount,
                color: parseFloat(co.approved_amount) >= 0 ? '#1d4ed8' : '#dc2626',
                weight: 400,
                indent: true,
                date: co.approved_date,
              })),
              ...(summary.manual_adjustments_total !== 0 ? [{
                label: 'Manual Adjustments',
                value: summary.manual_adjustments_total,
                color: '#d97706',
                weight: 400,
              }] : []),
            ].map((row, i) => (
              <div key={i} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '10px 0', borderBottom: `1px solid ${t.tableBorder}`,
                paddingLeft: row.indent ? '20px' : '0',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {row.indent && <span style={{ color: t.textSecondary }}>+</span>}
                  <span style={{ fontSize: '14px', fontWeight: row.weight, color: t.heading }}>{row.label}</span>
                  {row.date && <span style={{ fontSize: '11px', color: t.textSecondary }}>{formatDate(row.date)}</span>}
                </div>
                <span style={{ fontSize: '14px', fontWeight: row.weight, color: row.color, fontVariantNumeric: 'tabular-nums' }}>
                  {parseFloat(row.value) >= 0 ? '+' : ''}{formatCurrency(row.value)}
                </span>
              </div>
            ))}
            {/* Total */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '14px 0 4px', borderTop: `2px solid ${t.tableBorder}`, marginTop: '4px',
            }}>
              <span style={{ fontSize: '15px', fontWeight: 700, color: t.heading }}>Current Contract Value</span>
              <span style={{ fontSize: '18px', fontWeight: 700, color: '#16a34a', fontVariantNumeric: 'tabular-nums' }}>
                {formatCurrency(summary.revised_contract_value)}
              </span>
            </div>
            {/* SOV vs Contract check */}
            {summary.sov_total !== summary.revised_contract_value && (
              <div style={{
                marginTop: '12px', padding: '10px 14px', borderRadius: '8px',
                backgroundColor: '#fef3c7', border: '1px solid #fcd34d', fontSize: '13px', color: '#92400e',
              }}>
                <span style={{ fontWeight: 600 }}>Note:</span> SOV total ({formatCurrency(summary.sov_total)}) does not match
                current contract value ({formatCurrency(summary.revised_contract_value)}).
                Difference: {formatCurrency(summary.revised_contract_value - summary.sov_total)}.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Change Orders Section */}
      {activeSection === 'changeOrders' && (
        <div style={{ border: `1px solid ${t.cardBorder}`, borderRadius: '12px', overflow: 'hidden', backgroundColor: t.bg }}>
          {/* Table Header */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '0.6fr 1.5fr 0.7fr 0.7fr 0.7fr 0.6fr 1fr',
            padding: '14px 16px', backgroundColor: t.bgAlt, borderBottom: `2px solid ${t.tableBorder}`,
            fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: t.textSecondary,
            gap: '6px',
          }}>
            <div>CO #</div>
            <div>Title</div>
            <div>Status</div>
            <div style={{ textAlign: 'right' }}>Requested</div>
            <div style={{ textAlign: 'right' }}>Approved</div>
            <div>Date</div>
            <div style={{ textAlign: 'right' }}>Actions</div>
          </div>

          {changeOrders.length === 0 ? (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: t.textSecondary, fontSize: '14px' }}>
              No change orders yet. Click "+ Change Order" to create one.
            </div>
          ) : (
            changeOrders.map((co, idx) => (
              <div key={co.id} style={{
                display: 'grid',
                gridTemplateColumns: '0.6fr 1.5fr 0.7fr 0.7fr 0.7fr 0.6fr 1fr',
                padding: '12px 16px', borderBottom: idx < changeOrders.length - 1 ? `1px solid ${t.tableBorder}` : 'none',
                alignItems: 'center', gap: '6px', transition: 'background-color 0.1s',
              }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = t.bgAlt}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <div style={{ fontSize: '13px', fontWeight: 600, color: '#3b82f6' }}>{co.change_order_number}</div>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 500, color: t.heading }}>{co.title}</div>
                  {co.description && (
                    <div style={{ fontSize: '11px', color: t.textSecondary, marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {co.description}
                    </div>
                  )}
                </div>
                <div>
                  <span style={{
                    ...getStatusBadge(co.status), padding: '3px 10px', borderRadius: '10px',
                    fontSize: '11px', fontWeight: 600, display: 'inline-block',
                  }}>
                    {co.status}
                  </span>
                </div>
                <div style={{ fontSize: '13px', color: t.heading, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {formatCurrency(co.requested_amount)}
                </div>
                <div style={{ fontSize: '13px', color: co.status === 'Approved' ? '#16a34a' : t.textSecondary, textAlign: 'right', fontWeight: co.status === 'Approved' ? 600 : 400, fontVariantNumeric: 'tabular-nums' }}>
                  {co.status === 'Approved' ? formatCurrency(co.approved_amount) : '–'}
                </div>
                <div style={{ fontSize: '12px', color: t.textSecondary }}>
                  {formatDate(co.approved_date || co.submitted_date || co.created_at)}
                </div>
                <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                  {co.status === 'Draft' && (
                    <>
                      <button onClick={() => handleStatusChange(co, 'Submitted')} style={{
                        padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: 600,
                        border: '1px solid #fcd34d', backgroundColor: '#fffbeb', color: '#d97706', cursor: 'pointer',
                      }}>Submit</button>
                      <button onClick={() => handleDeleteCO(co.id)} style={{
                        padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: 600,
                        border: '1px solid #fca5a5', backgroundColor: '#fef2f2', color: '#dc2626', cursor: 'pointer',
                      }}>Delete</button>
                    </>
                  )}
                  {co.status === 'Submitted' && (
                    <>
                      <button onClick={() => handleStatusChange(co, 'Approved')} style={{
                        padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: 600,
                        border: '1px solid #86efac', backgroundColor: '#f0fdf4', color: '#16a34a', cursor: 'pointer',
                      }}>Approve</button>
                      <button onClick={() => handleStatusChange(co, 'Rejected')} style={{
                        padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: 600,
                        border: '1px solid #fca5a5', backgroundColor: '#fef2f2', color: '#dc2626', cursor: 'pointer',
                      }}>Reject</button>
                    </>
                  )}
                  {(co.status === 'Approved' || co.status === 'Rejected') && (
                    <span style={{ fontSize: '12px', color: '#94a3b8', padding: '4px 0' }}>
                      {co.status === 'Approved' ? 'Added to SOV' : 'Closed'}
                    </span>
                  )}
                </div>
              </div>
            ))
          )}

          {/* Totals */}
          {changeOrders.length > 0 && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: '0.6fr 1.5fr 0.7fr 0.7fr 0.7fr 0.6fr 1fr',
              padding: '14px 16px', backgroundColor: t.bgAlt, borderTop: `2px solid ${t.tableBorder}`,
              fontSize: '13px', fontWeight: 700, color: t.heading, gap: '6px',
            }}>
              <div style={{ gridColumn: 'span 3' }}>TOTALS</div>
              <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {formatCurrency(changeOrders.reduce((s, co) => s + (parseFloat(co.requested_amount) || 0), 0))}
              </div>
              <div style={{ textAlign: 'right', color: '#16a34a', fontVariantNumeric: 'tabular-nums' }}>
                {formatCurrency(approvedCOs.reduce((s, co) => s + (parseFloat(co.approved_amount) || 0), 0))}
              </div>
              <div></div>
              <div></div>
            </div>
          )}
        </div>
      )}

      {/* Audit Log Section */}
      {activeSection === 'log' && (
        <div style={{ border: `1px solid ${t.cardBorder}`, borderRadius: '12px', overflow: 'hidden', backgroundColor: t.bg }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 2fr 0.8fr 0.8fr 0.8fr 0.8fr',
            padding: '14px 16px', backgroundColor: t.bgAlt, borderBottom: `2px solid ${t.tableBorder}`,
            fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: t.textSecondary,
            gap: '6px',
          }}>
            <div>Date</div>
            <div>Description</div>
            <div>Type</div>
            <div style={{ textAlign: 'right' }}>Before</div>
            <div style={{ textAlign: 'right' }}>Change</div>
            <div style={{ textAlign: 'right' }}>After</div>
          </div>

          {log.length === 0 ? (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: t.textSecondary, fontSize: '14px' }}>
              No contract value changes recorded yet.
            </div>
          ) : (
            log.map((entry, idx) => (
              <div key={entry.id} style={{
                display: 'grid',
                gridTemplateColumns: '1fr 2fr 0.8fr 0.8fr 0.8fr 0.8fr',
                padding: '12px 16px', borderBottom: idx < log.length - 1 ? `1px solid ${t.tableBorder}` : 'none',
                alignItems: 'center', gap: '6px', transition: 'background-color 0.1s',
              }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = t.bgAlt}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <div style={{ fontSize: '12px', color: t.textSecondary }}>
                  {formatDateTime(entry.created_at)}
                </div>
                <div>
                  <div style={{ fontSize: '13px', color: t.heading }}>{entry.description}</div>
                  {entry.changed_by && (
                    <div style={{ fontSize: '11px', color: t.textSecondary, marginTop: '1px' }}>by {entry.changed_by}</div>
                  )}
                </div>
                <div>
                  <span style={{
                    ...getChangeTypeBadge(entry.change_type), padding: '2px 8px', borderRadius: '8px',
                    fontSize: '10px', fontWeight: 600, display: 'inline-block', whiteSpace: 'nowrap',
                  }}>
                    {entry.change_type}
                  </span>
                </div>
                <div style={{ fontSize: '13px', color: t.textSecondary, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {formatCurrency(entry.amount_before)}
                </div>
                <div style={{
                  fontSize: '13px', fontWeight: 600, textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                  color: parseFloat(entry.amount_change) >= 0 ? '#16a34a' : '#dc2626',
                }}>
                  {parseFloat(entry.amount_change) >= 0 ? '+' : ''}{formatCurrency(entry.amount_change)}
                </div>
                <div style={{ fontSize: '13px', fontWeight: 600, color: t.heading, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {formatCurrency(entry.amount_after)}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* SOV Line Item Changes Section */}
      {activeSection === 'sovChanges' && (
        <div style={{ border: `1px solid ${t.cardBorder}`, borderRadius: '12px', overflow: 'hidden', backgroundColor: t.bg }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '0.8fr 0.6fr 2fr 0.8fr 0.8fr 0.8fr 1.2fr',
            padding: '14px 16px', backgroundColor: t.bgAlt, borderBottom: `2px solid ${t.tableBorder}`,
            fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: t.textSecondary,
            gap: '6px',
          }}>
            <div>Date</div>
            <div>SOV Code</div>
            <div>Description</div>
            <div>Field</div>
            <div>Old Value</div>
            <div>New Value</div>
            <div>Reason</div>
          </div>

          {sovChangeLog.length === 0 ? (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: t.textSecondary, fontSize: '14px' }}>
              No SOV line item changes recorded yet. Double-click any SOV item number to edit properties.
            </div>
          ) : (
            sovChangeLog.map((entry, idx) => {
              const isValueChange = entry.field_changed === 'contract_amount';
              return (
                <div key={entry.id} style={{
                  display: 'grid',
                  gridTemplateColumns: '0.8fr 0.6fr 2fr 0.8fr 0.8fr 0.8fr 1.2fr',
                  padding: '12px 16px', borderBottom: idx < sovChangeLog.length - 1 ? `1px solid ${t.tableBorder}` : 'none',
                  alignItems: 'center', gap: '6px', transition: 'background-color 0.1s',
                  backgroundColor: isValueChange ? '#fffbeb' : 'transparent',
                }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = isValueChange ? '#fef3c7' : t.bgAlt}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = isValueChange ? '#fffbeb' : 'transparent'}
                >
                  <div style={{ fontSize: '12px', color: t.textSecondary }}>
                    {new Date(entry.changed_at).toLocaleDateString()} {new Date(entry.changed_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#3b82f6' }}>
                    {entry.sov_code}
                  </div>
                  <div style={{ fontSize: '12px', color: t.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {entry.description}
                  </div>
                  <div>
                    <span style={{
                      padding: '2px 8px', borderRadius: '8px', fontSize: '10px', fontWeight: 600,
                      display: 'inline-block', whiteSpace: 'nowrap',
                      backgroundColor: isValueChange ? '#FFF3E0' : '#F1F5F9',
                      color: isValueChange ? '#E65100' : '#64748B',
                      border: `1px solid ${isValueChange ? '#FFB74D' : '#CBD5E1'}`,
                    }}>
                      {entry.field_changed.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <div style={{ fontSize: '12px', color: '#dc2626', fontVariantNumeric: 'tabular-nums' }}>
                    {isValueChange ? formatCurrency(entry.old_value) : (entry.old_value || '–')}
                  </div>
                  <div style={{ fontSize: '12px', fontWeight: 500, color: '#16a34a', fontVariantNumeric: 'tabular-nums' }}>
                    {isValueChange ? formatCurrency(entry.new_value) : (entry.new_value || '–')}
                  </div>
                  <div style={{ fontSize: '12px', color: '#d97706', fontStyle: entry.change_reason ? 'italic' : 'normal' }}>
                    {entry.change_reason || '–'}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Create Change Order Modal */}
      {showCOModal && (
        <div className="modal-overlay" onClick={() => setShowCOModal(false)}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: t.bg, borderRadius: '16px', width: '520px', maxHeight: '90vh',
            overflow: 'hidden', display: 'flex', flexDirection: 'column',
            boxShadow: '0 25px 60px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.05)',
            animation: 'slideUp 0.25s ease',
          }}>
            <div style={{ padding: '24px 28px 16px', borderBottom: `1px solid ${t.tableBorder}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: t.heading }}>New Change Order</h2>
                  <p style={{ margin: '6px 0 0', fontSize: '13px', color: t.textSecondary }}>Create a change order that will affect the contract value when approved.</p>
                </div>
                <button onClick={() => setShowCOModal(false)} style={{
                  background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
                  color: t.textSecondary, fontSize: '22px', lineHeight: 1, borderRadius: '6px',
                }}>&times;</button>
              </div>
            </div>
            <form onSubmit={handleCreateCO} style={{ flex: 1, overflowY: 'auto', padding: '20px 28px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div style={mGroup}>
                  <label style={mLabel}>CO Number *</label>
                  <input type="text" value={coForm.change_order_number}
                    onChange={(e) => setCOForm({ ...coForm, change_order_number: e.target.value })}
                    required placeholder="e.g., CO-001" style={mInput}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
                </div>
                <div style={mGroup}>
                  <label style={mLabel}>Requested Amount *</label>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '14px', pointerEvents: 'none' }}>$</span>
                    <input type="number" step="0.01" value={coForm.requested_amount}
                      onChange={(e) => setCOForm({ ...coForm, requested_amount: e.target.value })}
                      required placeholder="0.00" style={{ ...mInput, paddingLeft: '28px' }}
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
                  </div>
                </div>
              </div>
              <div style={mGroup}>
                <label style={mLabel}>Title *</label>
                <input type="text" value={coForm.title}
                  onChange={(e) => setCOForm({ ...coForm, title: e.target.value })}
                  required placeholder="e.g., Additional Electrical Work" style={mInput}
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
              </div>
              <div style={mGroup}>
                <label style={mLabel}>Description</label>
                <textarea value={coForm.description}
                  onChange={(e) => setCOForm({ ...coForm, description: e.target.value })}
                  rows="3" placeholder="Describe the change order..." style={{ ...mInput, resize: 'vertical', fontFamily: 'inherit' }} />
              </div>
              <div style={mGroup}>
                <label style={mLabel}>Notes</label>
                <textarea value={coForm.notes}
                  onChange={(e) => setCOForm({ ...coForm, notes: e.target.value })}
                  rows="2" placeholder="Optional notes..." style={{ ...mInput, resize: 'vertical', fontFamily: 'inherit' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', paddingTop: '10px', borderTop: `1px solid ${t.tableBorder}` }}>
                <button type="button" onClick={() => setShowCOModal(false)} style={{
                  padding: '10px 20px', borderRadius: '10px', fontSize: '14px', fontWeight: 600,
                  border: `1px solid ${t.cardBorder}`, backgroundColor: t.bg, color: t.textSecondary, cursor: 'pointer',
                }}>Cancel</button>
                <button type="submit" style={{
                  padding: '10px 20px', borderRadius: '10px', fontSize: '14px', fontWeight: 600,
                  border: 'none', backgroundColor: '#3b82f6', color: '#fff', cursor: 'pointer',
                }}>Create Change Order</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Approve Modal */}
      {showApproveModal && selectedCO && (
        <div className="modal-overlay" onClick={() => setShowApproveModal(false)}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: t.bg, borderRadius: '16px', width: '440px',
            boxShadow: '0 25px 60px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.05)',
            animation: 'slideUp 0.25s ease',
          }}>
            <div style={{ padding: '24px 28px 16px', borderBottom: `1px solid ${t.tableBorder}` }}>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: t.heading }}>Approve Change Order</h2>
              <p style={{ margin: '6px 0 0', fontSize: '13px', color: t.textSecondary }}>
                This will add the approved amount to the contract value and create a new SOV line.
              </p>
            </div>
            <div style={{ padding: '20px 28px' }}>
              <div style={{ padding: '12px 16px', borderRadius: '10px', backgroundColor: t.bgAlt, border: `1px solid ${t.cardBorder}`, marginBottom: '16px' }}>
                <div style={{ fontSize: '13px', color: t.textSecondary, marginBottom: '4px' }}>{selectedCO.change_order_number}</div>
                <div style={{ fontSize: '15px', fontWeight: 600, color: t.heading }}>{selectedCO.title}</div>
                <div style={{ fontSize: '13px', color: t.textSecondary, marginTop: '4px' }}>Requested: {formatCurrency(selectedCO.requested_amount)}</div>
              </div>
              <div style={mGroup}>
                <label style={mLabel}>Approved Amount *</label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '14px', pointerEvents: 'none' }}>$</span>
                  <input type="number" step="0.01" value={approvedAmount}
                    onChange={(e) => setApprovedAmount(e.target.value)}
                    required style={{ ...mInput, paddingLeft: '28px' }}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', paddingTop: '10px', borderTop: `1px solid ${t.tableBorder}` }}>
                <button onClick={() => setShowApproveModal(false)} style={{
                  padding: '10px 20px', borderRadius: '10px', fontSize: '14px', fontWeight: 600,
                  border: `1px solid ${t.cardBorder}`, backgroundColor: t.bg, color: t.textSecondary, cursor: 'pointer',
                }}>Cancel</button>
                <button onClick={handleApprove} style={{
                  padding: '10px 20px', borderRadius: '10px', fontSize: '14px', fontWeight: 600,
                  border: 'none', backgroundColor: '#16a34a', color: '#fff', cursor: 'pointer',
                }}>Approve &amp; Add to SOV</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manual Adjustment Modal */}
      {showAdjustModal && (
        <div className="modal-overlay" onClick={() => setShowAdjustModal(false)}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: t.bg, borderRadius: '16px', width: '480px',
            boxShadow: '0 25px 60px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.05)',
            animation: 'slideUp 0.25s ease',
          }}>
            <div style={{ padding: '24px 28px 16px', borderBottom: `1px solid ${t.tableBorder}` }}>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: t.heading }}>Manual Adjustment</h2>
              <p style={{ margin: '6px 0 0', fontSize: '13px', color: t.textSecondary }}>
                Manually adjust the contract value. Use negative amounts to decrease.
              </p>
            </div>
            <form onSubmit={handleManualAdjustment} style={{ padding: '20px 28px' }}>
              <div style={mGroup}>
                <label style={mLabel}>Description *</label>
                <input type="text" value={adjustForm.description}
                  onChange={(e) => setAdjustForm({ ...adjustForm, description: e.target.value })}
                  required placeholder="e.g., Contract correction, scope reduction..." style={mInput}
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div style={mGroup}>
                  <label style={mLabel}>Amount (+/-) *</label>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '14px', pointerEvents: 'none' }}>$</span>
                    <input type="number" step="0.01" value={adjustForm.amount_change}
                      onChange={(e) => setAdjustForm({ ...adjustForm, amount_change: e.target.value })}
                      required placeholder="0.00" style={{ ...mInput, paddingLeft: '28px' }}
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
                  </div>
                </div>
                <div style={mGroup}>
                  <label style={mLabel}>Changed By</label>
                  <input type="text" value={adjustForm.changed_by}
                    onChange={(e) => setAdjustForm({ ...adjustForm, changed_by: e.target.value })}
                    placeholder="Your name" style={mInput}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', paddingTop: '10px', borderTop: `1px solid ${t.tableBorder}` }}>
                <button type="button" onClick={() => setShowAdjustModal(false)} style={{
                  padding: '10px 20px', borderRadius: '10px', fontSize: '14px', fontWeight: 600,
                  border: `1px solid ${t.cardBorder}`, backgroundColor: t.bg, color: t.textSecondary, cursor: 'pointer',
                }}>Cancel</button>
                <button type="submit" style={{
                  padding: '10px 20px', borderRadius: '10px', fontSize: '14px', fontWeight: 600,
                  border: 'none', backgroundColor: '#d97706', color: '#fff', cursor: 'pointer',
                }}>Record Adjustment</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog {...confirmDialogProps} />
    </div>
  );
};

export default ContractValueTab;
